import { CurrencyPipe, formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Paginator } from 'primeng/paginator';
import { DemoTaxReportResponse } from 'src/app/_data/tax-report';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { FilterServiceService } from 'src/app/_services/filter-service.service';
import { GlobalService } from 'src/app/_services/global.service';
import { TaxReportService } from 'src/app/_services/tax-report.service';
import { environment } from 'src/environments/environment';

/**
 * State-wise Tax Report — one row per registered state with taxable/non-taxable
 * order counts, gross sales, tax collected, refunded tax and net tax (what the
 * merchant owes the state). Follows the same main-app report conventions as the
 * Payout Report: TaxReport endpoint with a !environment.production demo fallback,
 * 'Tax Report' export config in reports[], and filter persistence under 'Reports'.
 */
@Component({
  selector: 'app-tax-report',
  templateUrl: './tax-report.component.html',
  styleUrls: ['./tax-report.component.scss']
})
export class TaxReportComponent implements OnInit {

  _globals = GlobalService;

  page = 0;
  rows = 10;
  first = 0;
  totalRecords = 0;
  dataList: any[] = null;
  orderParam = '';
  orderBy = 'asc';
  locationIds: string[] = [];
  isLoading = false;
  skeletonRows: { index: number }[] = [];

  colHeader = this._abd.reports.filter(x => x.paraReportName == 'Tax Report')[0].summaryHeader;
  colflds = this._abd.reports.filter(x => x.paraReportName == 'Tax Report')[0].colflds;

  @ViewChild('p', { static: false }) paginator: Paginator;

  constructor(
    public _abd: AppBaseDataService,
    public _filterService: FilterServiceService,
    private currencyPipe: CurrencyPipe,
    private _reportsService: TaxReportService,
  ) { }

  ngOnInit(): void {
    this._globals.componentName = 'Reports';
    this._globals.secondTitle = 'Tax Report';
  }

  ngAfterViewInit() {
    let ti = null;
    ti = setInterval(() => {
      const ppages = this._filterService.getPages(this._globals.componentName);
      if (ppages) {
        let lastPage = parseInt(ppages.page, 10);
        if (isNaN(lastPage) || lastPage < 0) lastPage = 0;
        this.page = lastPage;
        this.rows = ppages.rows || this.rows;
        this.first = lastPage * this.rows;
      }

      const filter = this._filterService.getFilters(this._globals.componentName);
      if (filter) {
        if (filter.filterValue) this._filterService.searchText = filter.filterValue;
      }

      const oorderby = this._filterService.getOrderby(this._globals.componentName);
      if (oorderby) {
        this.orderParam = oorderby.orderParam;
        this.orderBy = oorderby.orderBy;
      }

      clearInterval(ti);
      this.getDataList();
    }, 500);
  }

  ngOnDestroy() {
    this._globals.componentName = '';
    this._globals.secondTitle = '';
  }

  /**
   * Build LocationIds from the logged-in user (GlobalService.userInfo.userLocations) —
   * the source of truth for which locations this user can see — falling back to the
   * selected-location filter / master locations list if that array isn't populated.
   */
  private getLocationsList() {
    const userLocations = GlobalService.userInfo?.userLocations;
    if (Array.isArray(userLocations) && userLocations.length) {
      this.locationIds = userLocations.map((x: any) => String(x));
      return;
    }

    const self = this;
    this.locationIds = [];
    let selLocation: any = this._filterService.selectedLocation;
    if (!selLocation || selLocation.length < 1) {
      selLocation = JSON.parse(JSON.stringify(this._abd.locations || []));
    }
    selLocation = (typeof selLocation != 'undefined' && selLocation instanceof Array) ? selLocation : [selLocation];
    selLocation.forEach((value: any) => {
      if (value?.locationId !== undefined && value?.locationId !== null) {
        self.locationIds.push(value.locationId.toString());
      }
    });
  }

  /** The `Parameter` payload for the unified GetReport call (matches SalesTaxReport's shape). */
  private buildParameter() {
    this.getLocationsList();
    const op = this._filterService.selectedProductSellTypeFilter;
    const orderPlacement = op?.Value || 'all';
    return {
      StartDate: formatDate(this._filterService.dateRange[0], 'MM/dd/yyyy', 'en-US'),
      EndDate: formatDate(this._filterService.dateRange[1], 'MM/dd/yyyy', 'en-US'),
      LocationIds: this.locationIds,
      OrderPlacement: orderPlacement,
    };
  }

  /** Normalize a row to the table's field names, reading both camel/Pascal casing defensively. */
  private mapRow(row: any) {
    return {
      state: row?.state ?? row?.State ?? '',
      rate: Number(row?.rate ?? row?.Rate ?? 0),
      // Hidden from the table for now, kept in the mapped row so re-enabling the column needs no data change. TODO re-enable.
      taxableOrders: Number(row?.taxableOrders ?? row?.TaxableOrders ?? 0),
      grossSales: Number(row?.grossSales ?? row?.GrossSales ?? 0),
      taxCollected: Number(row?.taxCollected ?? row?.TaxCollected ?? 0),
      refundedTax: Number(row?.refundedTax ?? row?.RefundedTax ?? 0),
      netTax: Number(row?.netTax ?? row?.NetTax ?? 0),
    };
  }

  private setSkeletonRows(): void {
    const length = this.rows > 0 ? this.rows : 10;
    this.skeletonRows = Array.from({ length }, (_, index) => ({ index }));
  }

  getDataList() {
    this.isLoading = true;
    this.setSkeletonRows();
    this._abd.showSpinner();
    const parameter = this.buildParameter();

    this._reportsService.getTaxReport(parameter).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this._abd.hideSpinner();
        const rows = res?.data ?? res?.Data;
        if (res?.success !== false && Array.isArray(rows)) {
          this.dataList = rows.map((r: any) => this.mapRow(r));
          // This report returns every matching row in one page (no server-side paging), so the
          // row count is the source of truth — don't trust an API totalRecords/TotalRecords field
          // that may be 0/missing even when rows came back (`??` doesn't catch a literal 0).
          const apiTotal = Number(res?.totalRecords ?? res?.TotalRecords);
          this.totalRecords = apiTotal > 0 ? apiTotal : this.dataList.length;
        } else {
          this.handleListErrorFallback();
        }
      },
      error: () => {
        this.handleListErrorFallback();
      }
    });

    this._filterService.updateFilters(this._globals.componentName, parameter);
    this._filterService.updatePages(this._globals.componentName, { page: this.page, rows: this.rows });
  }

  private handleListErrorFallback() {
    this.isLoading = false;
    this._abd.hideSpinner();
    if (!environment.production) {
      // No real backend in dev — render the static mock so the report is reviewable.
      this.dataList = DemoTaxReportResponse.data || [];
      this.totalRecords = this.dataList.length;
    } else {
      this.dataList = [];
      this.totalRecords = 0;
    }
  }

  refrestDataList() {
    this.getDataList();
  }

  callbackFuncFromFilter(value: any) {
    this.getDataList();
  }

  filterButtonPressed() {
    this.getDataList();
  }

  onPageChange(e) {
    this.page = e.page;
    this.rows = e.rows;
    this.first = e.first;
    this.getDataList();
  }

  customSort(e) {
    const last = this.orderParam;
    switch (e.sortField) {
      case 'state':
        this.orderParam = 'state';
        break;
      case 'grossSales':
        this.orderParam = 'grossSales';
        break;
      case 'taxCollected':
        this.orderParam = 'taxCollected';
        break;
      case 'netTax':
        this.orderParam = 'netTax';
        break;
      default:
        this.orderParam = '';
    }
    if (this.orderParam !== '') {
      this.orderBy = e.sortOrder == 1 ? 'asc' : 'desc';
      if (last !== this.orderParam) this.orderBy = 'asc';
      this._filterService.updateOrderby(this._globals.componentName, { orderParam: this.orderParam, orderBy: this.orderBy });
      this.getDataList();
    }
  }

  /** Plain integer total for the order-count columns. */
  calculateCountTotal(col: string): number {
    if (!this.dataList?.length) return 0;
    return this.dataList.reduce((sum, item) => sum + (item[col] || 0), 0);
  }

  /** Currency-formatted total for the money columns — uses the business currency
   * sign so the Total row matches the per-row currency (not a hardcoded $). */
  calculateCurrencyTotal(col: string) {
    if (!this.dataList?.length) return GlobalService.formatNumberWithCurrencySign(0);
    const total = this.dataList.reduce((sum, item) => sum + (item[col] || 0), 0);
    return GlobalService.formatNumberWithCurrencySign(total);
  }

  exportExcel() {
    this._abd.exportExcel(this.dataList, this.colHeader[0], this.colflds, 'Tax Report');
  }

  exportPdf() {
    this._abd.exportPdf(this.dataList, this.colHeader, this.colflds, 'Tax Report', true, false, false);
  }
}
